// The in-memory memory twin (M7-04; services/api/src/routes/memory.ts): memory review, the
// read-only memory proxy, promotions and Setup › Recall, with the real routes' rules: the
// `memory` add-on (the dispatcher's gate), members only (a client viewer 403), promotions decided
// by a person with a membership (the agency operator reads, decides nothing: 403), a decided
// promotion 409, accept → a page in a space (the pages twin makes it, naming the proposal), the
// status for managers only. Recall itself is never reached: the twin's "Recall" is a fixed list
// of records per company, searched by word, and its connection is the option `recall`
// (`connected` by default; `not_configured` is what a server without the partner key answers).
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, page, problem, validation } from "./fake";
import type { FakeCompany } from "./fake-data";
import { uid } from "./fake-data";
import { createMemoryAdminFake, FAKE_MEMORY_ADMIN_ROUTES } from "./fake-memory-admin";
import type { WorkBridge } from "./fake-work";

type Review = OperationOutput<"memory.review">;
type PromotionItem = Review["proposals"][number];
type Contradiction = Review["contradictions"][number];
type Conflict = Review["conflicts"][number];
type Decision = Review["decisions"][number];
type Lane = OperationOutput<"memory.lane">;
type LaneItem = Lane["items"][number];
type LaneKind = LaneItem["kind"];

/** What the twin's Recall does not take yet: the answers that wait on the memory service. */
const LANE_WAITING_NOTE =
  "Waiting on the memory service: your answer is kept and sent once it can take it";
type Hit = OperationOutput<"memory.search">["hits"][number];
type Status = OperationOutput<"recall.status">;
type PageKind = "page" | "sop" | "policy";

/**
 * Phase 1 inc 3: `not_set_up` is a connected company whose signed-in person cannot be read for
 * yet (no signing key on the server, or no Recall member): every company-level read says
 * connected, and `memory/search` says `not_set_up`. Only a server in `assertion` mode answers
 * `not_set_up`, so the fake's Setup › Recall says enforcement is on for it and off (the
 * `prefilter` default with no signing key) for every other state.
 */
export type FakeRecallState = "connected" | "not_configured" | "not_provisioned" | "not_set_up";

const NOT_SET_UP =
  "Memory isn't set up for you yet: your Recall member is created within a minute of joining. Try again shortly.";

/** The memory routes the twin serves (the dispatcher gates them on `memory`). */
export const FAKE_MEMORY_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "memory.review",
  "memory.proposalsBulk",
  "memory.captureSettings",
  "memory.captureUpdate",
  "memory.captureStats",
  "memory.conflictSettle",
  "memory.lane",
  "memory.laneDecide",
  "memory.laneBulk",
  "memory.laneSeeAll",
  "memory.decisionCalibration",
  "memory.search",
  "memory.catchUp",
  "memory.catchUpDismiss",
  "memoryPeople.card",
  "memoryPeople.profileAccept",
  "memoryPeople.profileDiscard",
  "memoryPeople.successions",
  "memoryPeople.successionAnswer",
  "memoryPeople.successionSettle",
  "memoryPeople.roles",
  "memoryPeople.retirePreview",
  "memoryPeople.actingEnd",
  "memoryPeople.onboarding",
  "memoryPeople.onboardingStart",
  "memoryPeople.onboardingDismiss",
  "memoryPeople.charter",
  "memoryPeople.charterSet",
  "memoryPeople.charterFromVision",
  "promotions.list",
  "promotions.accept",
  "promotions.reject",
  "recall.status",
  "recall.kbList",
  "recall.kbUpdate",
  "recall.kbSyncNow",
  ...FAKE_MEMORY_ADMIN_ROUTES,
]);

const NOT_CONFIGURED =
  "Recall is not connected on this server yet: the operator sets its address and partner key.";
const NOT_PROVISIONED =
  "Recall is connected, but this company's memory is not set up yet (it is provisioned within a minute of the add-on switching on).";

type Record_ = {
  scope: "company" | "space";
  type: string;
  title: string;
  text: string;
  why: string | null;
  /** RECALL-FOLLOWUP: found through an atom of this record (shown as the record). */
  atom?: string;
};

const RECORDS: Record_[] = [
  {
    scope: "company",
    type: "Procedure",
    title: "Screening calls take twenty minutes",
    text: "Every first screening call is booked for twenty minutes, with the scorecard open, and the notes go on the candidate the same day.",
    why: "Longer calls did not raise the pass rate in the spring review",
  },
  {
    scope: "company",
    type: "Decision",
    title: "One applicant tracker for every client",
    text: "Acme Recruiting runs all client searches in one applicant tracker; per-client spreadsheets are retired.",
    why: "Two placements were lost to a stale spreadsheet",
  },
  {
    scope: "space",
    type: "Lesson",
    title: "Reply to candidates within two days",
    text: "Candidates who wait more than two days for a first reply drop out three times as often.",
    why: null,
    atom: "a:5c1e9a07d2b4",
  },
  {
    scope: "company",
    type: "Principle",
    title: "Never send a CV without the candidate's consent",
    text: "A candidate's CV goes to a client only after the candidate agreed to that client, in writing.",
    why: "Client trust and data protection",
  },
];

type KbSpace = OperationOutput<"recall.kbList">["spaces"][number];

type State = {
  promotions: PromotionItem[];
  contradictions: Contradiction[];
  conflicts: Conflict[];
  decisions: Decision[];
  /** RECALL-KB: each space's Sync to Recall switch and last sync, by space id. */
  kb: Map<string, KbSpace>;
  /** MEMORY-HYGIENE: the pending memory proposals, as counts. */
  digest: { pending: number; exact: number; retired: number };
  /** RECALL-INBOX-LOOP: the Memory lane's cards in the owner's/department's reach, by id. */
  lane: LaneItem[];
  /** …and other departments' agents' cards, which only the owner's switch opens. */
  laneAll: LaneItem[];
  seeAll: boolean;
  /** Decisions checked this quarter (calibration). */
  checked: { worked: number; did_not: number; too_early: number };
  /** CAPTURE-LESS-NOISE: the company's capture override, by source. */
  capture: Partial<Record<CaptureSource, boolean>>;
};

type CaptureSource = "chat" | "task_threads" | "runs";
const CAPTURE_SOURCES: readonly CaptureSource[] = ["chat", "task_threads", "runs"];
/** The platform default (the server's `cp.memory_capture_setting` seed). */
const CAPTURE_DEFAULT: Record<CaptureSource, boolean> = {
  chat: true,
  task_threads: true,
  runs: false,
};

export function createMemoryFake(deps: {
  now: () => Date;
  companies: FakeCompany[];
  work: () => WorkBridge | null;
  recall: FakeRecallState;
  promote: (
    p: Principal,
    input: { space_id: string; title: string; kind: PageKind; markdown: string; ref: string },
  ) => string | Response;
}): Partial<Record<RouteId, Handler>> {
  const states = new Map<string, State>();
  const at = (minutesAgo = 0) => new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;
  const member = (p: Principal | null): p is Principal =>
    p?.roles.some((r) => r !== "client_viewer") === true;
  /** "See all agents' memory" is the company owner's (the twin's owner is its CEO): no manager or admin. */
  const ownerSeesAll = (p: Principal) => p.roles.includes("owner");
  const decides = (p: Principal) => membershipOf(p) !== null;
  /** The company's Recall is connected (whether or not this person is set up). */
  const live = deps.recall === "connected" || deps.recall === "not_set_up";
  const connection = () =>
    live
      ? { state: "connected" as const, detail: null }
      : deps.recall === "not_provisioned"
        ? { state: "not_provisioned" as const, detail: NOT_PROVISIONED }
        : { state: "not_configured" as const, detail: NOT_CONFIGURED };

  /** MEMORY-HYGIENE: the caller's digest (the twin's reviewers see every one). */
  const digestOf = (p: Principal, s: State) => ({
    pending: s.digest.pending,
    exact_merges: s.digest.exact,
    from_retired_agents: s.digest.retired,
    oldest_at: s.digest.pending ? at(60 * 24 * 3) : null,
    expire_days: 14,
    can_decide: decides(p),
  });

  const captureView = (s: State) => ({
    effective: { ...CAPTURE_DEFAULT, ...s.capture },
    platform: CAPTURE_DEFAULT,
    override: Object.keys(s.capture).length ? { ...s.capture } : null,
    quiet_minutes: 30,
    max_chars: 24_000,
  });

  function stateOf(c: FakeCompany): State {
    let s = states.get(c.id);
    if (s) return s;
    s = {
      promotions: [],
      contradictions: [],
      conflicts: [],
      decisions: [],
      kb: new Map(),
      digest: { pending: 0, exact: 0, retired: 0 },
      capture: {},
      lane: [],
      laneAll: [],
      seeAll: false,
      checked: { worked: 0, did_not: 0, too_early: 0 },
    };
    states.set(c.id, s);
    // A server without Recall has received nothing from it: the review is empty.
    if (live) seed(c, s);
    return s;
  }

  function seed(c: FakeCompany, s: State) {
    const space = deps.work()?.spacesOf(c.id)?.[0] ?? null;
    s.digest = { pending: 14, exact: 6, retired: 3 };
    const scope = space
      ? { kind: "space" as const, id: space.id, name: space.name }
      : { kind: "company" as const, id: c.id, name: c.name };
    const mk = (
      i: number,
      title: string,
      body: string,
      kind: string,
      state: PromotionItem["state"] = "pending",
    ): PromotionItem => ({
      company_id: c.id,
      id: uid(0x7c00, i),
      recall_ref: `dr_demo${i}/p1`,
      recall_bucket_id: `bk_demo${i}`,
      scope_kind: scope.kind,
      scope_id: scope.id,
      proposal_kind: kind,
      suggested_title: title,
      body,
      state,
      reviewer_seat_id: null,
      approval_id: null,
      page_id: null,
      decided_at: state === "pending" ? null : at(60 * 24),
      decided_by_membership_id: null,
      created_at: at(60 * (3 + i)),
      updated_at: at(60 * (3 + i)),
      scope_name: scope.name,
      can_decide: false,
    });
    s.promotions.push(
      mk(
        1,
        "Screening call checklist",
        "1. Confirm the role and salary band\n2. Ask the three knockout questions\n3. Book the next step before hanging up",
        "procedure",
      ),
      mk(
        2,
        "Candidate consent before sharing a CV",
        "A CV goes to a client only after the candidate agreed to that client in writing.",
        "rule",
      ),
      mk(
        3,
        "Weekly pipeline review",
        "Every Monday the team reviews the open roles.",
        "promote",
        "rejected",
      ),
    );
    s.contradictions.push({
      id: uid(0x7d00, 1),
      recall_event_id: "ev_demo1",
      scope: { kind: scope.kind, id: scope.id, name: scope.name },
      a: { type: "Decision", title: "Screening calls take twenty minutes" },
      b: { type: "Decision", title: "Screening calls take thirty minutes" },
      received_at: at(90),
    });
    // RECALL-CONTRADICTIONS: two open pairs, one with Recall's own suggestion
    const conflict = (
      i: number,
      a: [string, string, string],
      b: [string, string, string],
      why: string,
      suggestion: Conflict["suggestion"],
    ): Conflict => ({
      id: uid(0x7f00, i),
      scope: { kind: scope.kind, id: scope.id, name: scope.name },
      pair_id: `pair_demo${i}`,
      a: {
        type: a[0],
        title: a[1],
        abstract: a[2],
        updated: at(60 * 24 * 20),
        author: "Dana Ortiz",
      },
      b: {
        type: b[0],
        title: b[1],
        abstract: b[2],
        updated: at(60 * 24 * 2),
        author: "Recruiter (DE)",
      },
      why,
      suggestion,
      pairs: 1,
      since: at(60 * 24 * 2),
      state: "open",
      action: null,
      condition: null,
      decided_at: null,
      recall_synced: false,
      recall_note: null,
      recall_retry_at: null,
      can_decide: false,
      keep_a_needs_owner: false,
      keep_b_needs_owner: false,
      routed_to_owner: false,
      routed_by: null,
    });
    s.conflicts.push(
      conflict(
        1,
        [
          "Decision",
          "Screening calls take twenty minutes",
          "First screening calls are booked for twenty minutes.",
        ],
        [
          "Decision",
          "Screening calls take thirty minutes",
          "From November, first screening calls are booked for thirty minutes.",
        ],
        "The later decision changed the call length.",
        {
          winner: "b",
          category: "superseded_by_later_decision",
          reason: "The later record moves the earlier one on.",
          source: "recall",
        },
      ),
      conflict(
        2,
        ["Preference", "Clients get CVs as PDF", "Every client receives CVs as a PDF attachment."],
        [
          "Preference",
          "Northwind gets CVs as Word files",
          "Northwind's hiring manager edits CVs, so they get Word files.",
        ],
        "Two statements about how CVs are sent.",
        null,
      ),
    );
    s.decisions.push({
      company_id: c.id,
      id: uid(0x7e00, 1),
      title: "One applicant tracker for every client",
      context: null,
      options: [],
      decision: "adopted",
      why: null,
      decided_by_seat_id: null,
      review_at: null,
      expected_outcome: null,
      outcome_card_at: null,
      outcome: null,
      outcome_at: null,
      outcome_by_membership_id: null,
      outcome_notice: null,
      source: "recall",
      source_ref: "ev_demo2",
      share_to_portfolio: false,
      recall_ref: "Decision:One applicant tracker for every client",
      actor_kind: "system",
      actor_membership_id: null,
      created_at: at(60 * 26),
      updated_at: at(60 * 26),
    });
    seedLane(s, scope.name);
  }

  /** RECALL-INBOX-LOOP: the lane as the server would build it for the twin's owner. */
  function seedLane(s: State, scopeName: string) {
    const item = (
      i: number,
      kind: LaneKind,
      x: Partial<LaneItem> & { title: string },
    ): LaneItem => {
      const ref = uid(0x7b00, i);
      return {
        id: `${kind}:${ref}`,
        kind,
        ref_id: ref,
        detail: null,
        proposed_text: null,
        scope_name: scopeName,
        from: "Recruiter (DE)",
        since: at(60 * (4 + i)),
        why_here: "yours",
        state: "open",
        waiting_note: null,
        can_decide: true,
        can_edit: kind !== "contradiction" && kind !== "settle" && kind !== "outcome",
        can_bulk: kind === "lesson" || kind === "dream" || kind === "rule",
        needs_service: kind === "rule",
        conflict: null,
        promotion: null,
        outcome: null,
        ...x,
      };
    };
    const [c1, c2] = s.conflicts;
    s.lane = [
      item(1, "lesson", {
        title: "Candidates who wait over two days for a reply drop out three times as often",
        detail: "Seen across 41 screening threads this quarter.",
      }),
      item(2, "dream", {
        title: "Merge the two screening-call notes into one",
        detail: "The notes say the same thing in different words.",
        from: "Memory tidy-up",
      }),
      item(3, "rule", {
        title: "Always ask for consent before a CV goes to a client",
        proposed_text:
          "Always ask for the candidate's consent before a CV goes to a client, and keep the consent note on the candidate's record.",
        detail: "Held back as a standing rule once you confirm it.",
      }),
      item(4, "candidate", {
        title: "Screening call checklist",
        promotion: s.promotions[0] ?? null,
        can_bulk: false,
      }),
      ...(c1
        ? [
            item(5, "settle", {
              title: c1.a.title,
              detail: c1.why,
              from: null,
              conflict: c1,
              ref_id: c1.id,
              id: `settle:${c1.id}`,
              can_bulk: false,
            }),
          ]
        : []),
      ...(c2
        ? [
            item(6, "contradiction", {
              title: c2.a.title,
              detail: c2.why,
              from: null,
              conflict: c2,
              ref_id: c2.id,
              id: `contradiction:${c2.id}`,
              can_bulk: false,
            }),
          ]
        : []),
      item(7, "outcome", {
        title: "One applicant tracker for every client",
        detail: "Checked after two weeks.",
        from: null,
        scope_name: null,
        can_bulk: false,
        outcome: {
          decided_on: at(60 * 24 * 14),
          expected: "Fewer lost placements from stale sheets",
          check_at: at(60),
        },
      }),
      item(8, "lesson", {
        title: "Offer calls go better on Tuesdays",
        detail: "Sales has no person on this yet, so it came up to you.",
        scope_name: "Sales",
        why_here: "escalated",
        from: "Sales assistant (DE)",
      }),
    ];
    s.laneAll = [
      item(20, "lesson", {
        title: "Client invoices go out on the 25th",
        scope_name: "Finance",
        why_here: "owner_all",
        from: "Finance agent (AG)",
      }),
      item(21, "dream", {
        title: "Retire the old onboarding checklist",
        scope_name: "People",
        why_here: "owner_all",
        from: "People assistant (DE)",
      }),
    ];
    s.checked = { worked: 5, did_not: 1, too_early: 2 };
  }

  const view = (p: Principal, x: PromotionItem): PromotionItem => ({
    ...x,
    can_decide: x.state === "pending" && decides(p),
  });

  function decide(p: Principal | null, id: string | undefined, body: unknown, accept: boolean) {
    if (!p) return notFound();
    if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
    const s = stateOf(p.company);
    const x = s.promotions.find((r) => r.id === id);
    if (!x) return notFound();
    if (!decides(p))
      return problem(
        403,
        "forbidden",
        "Forbidden",
        "A person who is a member of the company decides promotions",
      );
    if (x.state !== "pending")
      return problem(409, "conflict", "Conflict", `The promotion was already ${x.state}`);
    const b = (body ?? {}) as {
      space_id?: unknown;
      title?: unknown;
      kind?: unknown;
      reason?: unknown;
    };
    let pageId: string | null = null;
    if (accept) {
      if (typeof b.space_id !== "string") return validation("space_id is required");
      const kind = (
        typeof b.kind === "string"
          ? b.kind
          : x.proposal_kind === "procedure"
            ? "sop"
            : x.proposal_kind === "rule"
              ? "policy"
              : "page"
      ) as PageKind;
      const made = deps.promote(p, {
        space_id: b.space_id,
        title: typeof b.title === "string" && b.title.trim() ? b.title.trim() : x.suggested_title,
        kind,
        markdown: x.body,
        ref: x.recall_ref,
      });
      if (made instanceof Response) return made;
      pageId = made;
    }
    x.state = accept ? "accepted" : "rejected";
    x.page_id = pageId;
    x.decided_at = at();
    x.decided_by_membership_id = membershipOf(p);
    x.updated_at = at();
    return json({ promotion: view(p, x), page_id: pageId });
  }

  const CARD_WINDOW = "2026-10-20";
  const closedCards = new Set<string>();
  function catchUp(p: Principal) {
    if (deps.recall === "not_set_up") return { state: "not_live" as const, card: null };
    if (deps.recall !== "connected") return { state: "off" as const, card: null };
    if (closedCards.has(`${p.company.id}|${membershipOf(p)}|${CARD_WINDOW}`))
      return { state: "none" as const, card: null };
    return {
      state: "card" as const,
      card: {
        window_key: CARD_WINDOW,
        from: "2026-10-20T09:00:00.000Z",
        to: at(),
        spaces: [
          {
            name: "Recruiting",
            lines: ["Two offers went out; one accepted", "Net 30 confirmed as the client default"],
          },
          { name: "Company", lines: ["Q4 priorities set; hiring is at risk"] },
        ],
        more: 3,
        info: "Since 20 Oct, from the spaces you read.",
      },
    };
  }

  // RECALL-ROLES-ONBOARDING: the people side. The twin's memory service does not list the person
  // doors yet (like production today): every act is kept and `doors` says "waiting".
  type Card = OperationOutput<"memoryPeople.card">;
  type Succession = OperationOutput<"memoryPeople.successionAnswer">;
  type Onboarding = OperationOutput<"memoryPeople.onboarding">;
  type Charter = OperationOutput<"memoryPeople.charter">;
  const WAITING = { state: "waiting" as const, label: "Waiting on memory service." };
  const doorsOf = () =>
    deps.recall === "connected" ? WAITING : { state: "off" as const, label: null };
  const people = {
    drafts: new Map<string, Card["profile"]>(),
    waiting: new Map<string, number>(),
    successions: new Map<string, Succession[]>(),
    onboarding: new Map<string, Onboarding>(),
    charters: new Map<string, Charter>(),
  };
  const mine = (p: Principal) => `${p.company.id}|${membershipOf(p)}`;
  const bump = (p: Principal) =>
    people.waiting.set(mine(p), (people.waiting.get(mine(p)) ?? 0) + 1);
  const draftOf = (p: Principal): Card["profile"] => {
    const k = mine(p);
    if (!people.drafts.has(k))
      people.drafts.set(k, {
        id: uid(0x7f00, 1),
        role: "Account Manager",
        kpis: ["Placements per month"],
        skills: ["Client onboarding", "Contracts"],
        text: "Runs client accounts and keeps placements on track.",
        expires_at: at(-60 * 24 * 14),
      });
    return people.drafts.get(k) ?? null;
  };
  function cardOf(p: Principal): Card {
    return {
      membership_id: membershipOf(p) ?? uid(0x7f40, 1),
      name: "You",
      is_self: true,
      seats: [{ seat_id: uid(0x7f10, 1), title: "Account Manager" }],
      kpis: ["Placements per month"],
      skills: ["Client onboarding", "Contracts"],
      knows_about: ["Client onboarding", "Contracts"],
      profile: draftOf(p),
      waiting: people.waiting.get(mine(p)) ?? 0,
      doors: doorsOf(),
      info: "From your seat: what you are accountable for and what to ask you about.",
    };
  }
  function successionsOf(p: Principal): Succession[] {
    const k = mine(p);
    if (!people.successions.has(k))
      people.successions.set(k, [
        {
          id: uid(0x7f20, 1),
          seat_id: uid(0x7f10, 2),
          seat_title: "Recruiting Lead",
          state: "waiting",
          from_name: "Dana",
          to_membership_id: membershipOf(p) ?? uid(0x7f40, 1),
          to_name: "You",
          detail: null,
          can_answer: true,
          can_settle: p.manager,
          created_at: at(30),
        },
      ]);
    return people.successions.get(k) ?? [];
  }
  const onboardingOf = (p: Principal): Onboarding =>
    people.onboarding.get(mine(p)) ?? {
      state: "offered",
      depth: p.manager ? "deep" : "quick",
      progress: { done: 0, total: p.manager ? 6 : 3 },
      can_dismiss: !p.manager,
      thread_id: null,
      note: null,
      doors: doorsOf(),
    };
  const charterKey = (p: Principal, kind: string, id: string) => `${p.company.id}|${kind}|${id}`;
  const charterOf = (p: Principal, kind: Charter["subject_kind"], id: string): Charter =>
    people.charters.get(charterKey(p, kind, id)) ?? {
      subject_kind: kind,
      subject_id: id,
      text: "",
      source: null,
      state: "none",
      detail: null,
      suggested: kind === "company_bucket" ? "Recruiting for agencies." : null,
      can_edit: p.manager,
      doors: doorsOf(),
    };

  function search(p: Principal, url: URL): Response {
    const q = (url.searchParams.get("q") ?? "").trim();
    const scope = url.searchParams.get("scope") ?? "company";
    if (!/^(company|(space|team|seat|task|person|de):[0-9a-f-]{36})$/i.test(scope))
      return validation("scope: company, or space|team|seat|task|person|de:{uuid}");
    const [kind, id] = scope.split(":");
    let named = p.company.name;
    if (kind === "task" && id) {
      const t = deps.work()?.taskOf(p.company.id, id);
      if (!t) return notFound();
      named = t.title;
    }
    const query = q || named;
    if (deps.recall === "not_set_up")
      return json({
        recall: { state: "not_set_up", detail: NOT_SET_UP },
        query,
        hits: [],
        buckets_searched: 0,
      });
    if (deps.recall !== "connected")
      return json({ recall: connection(), query, hits: [], buckets_searched: 0 });
    const space = deps.work()?.spacesOf(p.company.id)?.[0] ?? null;
    const words = query
      .toLowerCase()
      .split(/\W+/)
      .filter((w) => w.length > 2);
    const hits: Hit[] = RECORDS.filter((r) =>
      words.some((w) => `${r.title} ${r.text}`.toLowerCase().includes(w)),
    )
      .slice(0, 20)
      .map((r) => ({
        scope:
          r.scope === "space" && space
            ? { kind: "space", id: space.id, name: space.name }
            : { kind: "company", id: p.company.id, name: `${p.company.name}: company knowledge` },
        type: r.type,
        title: r.title,
        preview: r.text,
        why: r.why,
        atom: r.atom ?? null,
      }));
    return json({ recall: connection(), query, hits, buckets_searched: space ? 2 : 1 });
  }

  /** RECALL-KB: every non-personal space with its switch (DOCS-ALWAYS-IN-MEMORY: on by default). */
  function kbSpaces(p: Principal): KbSpace[] {
    const s = stateOf(p.company);
    return (deps.work()?.spacesOf(p.company.id) ?? [])
      .filter((x) => x.kind !== "personal")
      .map(
        (x) =>
          s.kb.get(x.id) ?? {
            space_id: x.id,
            name: x.name,
            kind: x.kind,
            pages: 3,
            sync_enabled: true,
            opt_out_reason: null,
            agent_access: "none",
            slug: null,
            files: 0,
            last_synced_at: null,
            last_attempt_at: null,
            sync_requested: false,
            last_put: [],
            last_gone: null,
            last_error: null,
            last_error_at: null,
          },
      );
  }

  /** What the worker's sync leaves on the row: every page sent the first time, none after. */
  function synced(k: KbSpace) {
    const first = k.last_synced_at === null;
    k.slug ??= `${k.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-docs-kb-${k.space_id.slice(-8)}`;
    k.files = k.pages;
    k.last_put = first ? ["candidate-screening-sop.md", "offer-and-close.md", "red-flags.md"] : [];
    k.last_gone = 0;
    k.last_synced_at = at();
    k.last_attempt_at = at();
    k.sync_requested = false;
  }

  function status(p: Principal): Status {
    const provisioned = live;
    return {
      recall: connection(),
      tenant: provisioned
        ? {
            recall_tenant_id: `tn_${p.company.slug}`,
            slug: p.company.slug,
            install_key_state: "acked",
            has_install_key: true,
            updated_at: at(60 * 24 * 3),
          }
        : null,
      webhook: {
        registered: false,
        recall_hook_id: null,
        callback_path: `/v1/companies/${p.company.id}/recall/callbacks`,
        updated_at: null,
      },
      // RECALL-FOLLOWUP: no webhook, so the worker polls Recall's event feed.
      events: provisioned
        ? {
            mode: "polling",
            state: "ok",
            detail: null,
            polled_at: at(1),
            seen: 12,
          }
        : { mode: "off", state: "waiting", detail: null, polled_at: null, seen: 0 },
      agents: provisioned
        ? [
            {
              subject_kind: "service_agent",
              subject_id: p.company.id,
              name: "CompanyOS service agent",
              recall_agent_id: `ag_${p.company.slug}`,
              key_state: "acked",
              has_key: true,
              updated_at: at(60 * 24 * 3),
            },
          ]
        : [],
      members: provisioned ? 4 : 0,
      member_identity: { local: 0, global: provisioned ? 4 : 0 },
      buckets: provisioned ? 6 : 0,
      last_reconciled_at: provisioned ? at(60 * 5) : null,
      person_enforcement:
        deps.recall === "not_set_up"
          ? { mode: "assertion", enforced: true, label: "Person-level memory enforcement: on" }
          : {
              mode: "prefilter",
              enforced: false,
              label:
                "Person-level memory enforcement: off — signing key not registered (CompanyOS filtering only)",
            },
    };
  }

  return {
    // RECALL-ADMIN-MEMORY: Setup › Memory (the lane's switch is the one switch).
    ...createMemoryAdminFake({
      now: deps.now,
      live,
      seeAll: (c) => stateOf(c).seeAll,
      laneOpen: (c) => stateOf(c).lane.filter((x) => x.state === "open").length,
    }),
    "memory.review": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const s = stateOf(p.company);
      return json({
        recall: connection(),
        proposals: s.promotions
          .filter((x) => x.state === "pending")
          .sort((a, b) => b.id.localeCompare(a.id))
          .map((x) => view(p, x)),
        contradictions: s.contradictions,
        decisions: s.decisions,
        proposal_digest: digestOf(p, s),
        conflicts: s.conflicts
          .filter((x) => x.state === "open")
          .map((x) => ({
            ...x,
            can_decide: decides(p),
            // keeping a side retires the other record (Dana's, a person's): owners only
            keep_b_needs_owner: !p.manager && x.a.author === "Dana Ortiz",
          })),
        conflicts_open: s.conflicts.filter((x) => x.state === "open").length,
        conflicts_unsent: s.conflicts.filter((x) => x.state === "settled" && !x.recall_synced),
        conflicts_unchecked: 0,
        conflicts_possibly_more: 0,
      } satisfies Review);
    },
    // RECALL-CONTRADICTIONS: a person settles one open pair (the twin keeps the verdict; Recall
    // does not let CompanyOS settle yet, as on the server).
    "memory.conflictSettle": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!decides(p))
        return problem(403, "forbidden", "Forbidden", "A person settles memory contradictions");
      const s = stateOf(p.company);
      const x = s.conflicts.find((r) => r.id === params.id);
      if (!x) return notFound();
      if (x.state !== "open")
        return problem(409, "conflict", "Conflict", `This contradiction is ${x.state}`);
      const b = (body ?? {}) as { action?: unknown; condition?: unknown; note?: unknown };
      const action = b.action;
      if (action !== "keep_a" && action !== "keep_b" && action !== "merge" && action !== "dismiss")
        return validation("action: keep_a, keep_b, merge or dismiss");
      const condition = typeof b.condition === "string" ? b.condition.trim() : "";
      if (action === "merge" && !condition)
        return validation("condition: say when each side holds");
      // a winner over another person's record needs `retire` on the space: routed, not settled
      if (action === "keep_b" && !p.manager && x.a.author === "Dana Ortiz") {
        Object.assign(x, { routed_to_owner: true, routed_by: "You" });
        return json({ ...x, keep_b_needs_owner: true });
      }
      Object.assign(x, {
        state: "settled" as const,
        action,
        condition: action === "merge" ? condition : null,
        decided_at: at(),
        recall_synced: false,
        recall_note:
          "Recall does not let CompanyOS settle for a person yet; kept here and sent once it does",
        recall_retry_at: at(-60 * 24),
        can_decide: false,
      });
      return json(x);
    },
    // MEMORY-HYGIENE: settle many at once (every pending one the caller reviews, in the twin).
    "memory.proposalsBulk": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!decides(p))
        return problem(403, "forbidden", "Forbidden", "A person settles memory proposals");
      const action = (body as { action?: unknown } | null)?.action;
      if (action !== "approve_exact_merges" && action !== "dismiss_retired_agents")
        return validation("action: approve_exact_merges or dismiss_retired_agents");
      const s = stateOf(p.company);
      const settled = action === "approve_exact_merges" ? s.digest.exact : s.digest.retired;
      s.digest = {
        pending: s.digest.pending - settled,
        exact: action === "approve_exact_merges" ? 0 : s.digest.exact,
        retired: action === "dismiss_retired_agents" ? 0 : s.digest.retired,
      };
      return json({ action, settled, digest: digestOf(p, s) });
    },
    // RECALL-BRIEFS: a person's catch-up card. The twin has Recall's `me` act live whenever Recall
    // is connected (so the preview shows the card); `not_set_up` says `not_live`; else `off`.
    "memory.catchUp": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return json(catchUp(p));
    },
    "memory.catchUpDismiss": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const key = (body as { window_key?: string } | null)?.window_key;
      if (!key) return validation("window_key: required");
      closedCards.add(`${p.company.id}|${membershipOf(p)}|${key}`);
      return json(catchUp(p));
    },
    // RECALL-INBOX-LOOP: the Memory lane (the owner's switch adds other departments' cards).
    "memory.lane": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const s = stateOf(p.company);
      const items = [...s.lane, ...(s.seeAll && ownerSeesAll(p) ? s.laneAll : [])];
      const counts: Record<string, number> = {};
      for (const x of items) if (x.state === "open") counts[x.kind] = (counts[x.kind] ?? 0) + 1;
      return json({
        items,
        counts,
        open: items.filter((x) => x.state === "open").length,
        waiting: items.filter((x) => x.state === "waiting").length,
        see_all: { available: ownerSeesAll(p), on: ownerSeesAll(p) && s.seeAll },
        escalation_days: 3,
        service_ready: false,
      } satisfies Lane);
    },
    "memory.laneDecide": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!decides(p)) return problem(403, "forbidden", "Forbidden", "A person decides these");
      const s = stateOf(p.company);
      const b = (body ?? {}) as { kind?: string; ref_id?: string; action?: string; text?: string };
      const id = `${b.kind}:${b.ref_id}`;
      const pool = [...s.lane, ...(s.seeAll && ownerSeesAll(p) ? s.laneAll : [])];
      const x = pool.find((r) => r.id === id);
      if (!x) return notFound();
      if (x.kind === "contradiction" || x.kind === "settle")
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "A contradiction is settled by a person, one by one",
        );
      if (x.kind === "candidate" && b.action !== "reject")
        return validation("A page candidate needs a place: accept it from its card");
      if (x.state === "waiting")
        return problem(409, "conflict", "Conflict", "Your answer is already waiting");
      if (x.kind === "outcome") {
        if (b.action !== "worked" && b.action !== "did_not" && b.action !== "too_early")
          return validation("action: worked, did_not or too_early");
        s.checked[b.action] += 1;
      } else if (b.action !== "approve" && b.action !== "reject" && b.action !== "edit_approve") {
        return validation("action: approve, reject or edit_approve");
      }
      if (b.action === "edit_approve" && !b.text?.trim())
        return validation("text: say what to keep");
      const waits = x.needs_service && b.action !== "reject";
      if (waits) {
        Object.assign(x, { state: "waiting" as const, waiting_note: LANE_WAITING_NOTE });
        return json({ id, result: "waiting", note: LANE_WAITING_NOTE });
      }
      s.lane = s.lane.filter((r) => r.id !== id);
      s.laneAll = s.laneAll.filter((r) => r.id !== id);
      return json({ id, result: "done", note: null });
    },
    "memory.laneBulk": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!decides(p)) return problem(403, "forbidden", "Forbidden", "A person decides these");
      const s = stateOf(p.company);
      const kinds = (body as { kinds?: string[] } | null)?.kinds;
      const pool = [...s.lane, ...(s.seeAll && ownerSeesAll(p) ? s.laneAll : [])];
      let approved = 0;
      let waiting = 0;
      let skipped = 0;
      for (const x of pool) {
        if (x.state !== "open") continue;
        if (!x.can_bulk || (kinds && !kinds.includes(x.kind))) {
          skipped += 1;
          continue;
        }
        if (x.needs_service) {
          Object.assign(x, { state: "waiting" as const, waiting_note: LANE_WAITING_NOTE });
          waiting += 1;
        } else {
          s.lane = s.lane.filter((r) => r.id !== x.id);
          s.laneAll = s.laneAll.filter((r) => r.id !== x.id);
          approved += 1;
        }
      }
      return json({ approved, waiting, skipped });
    },
    "memory.laneSeeAll": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!ownerSeesAll(p))
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "Only the owner or CEO sees all agents' memory",
        );
      const on = (body as { on?: unknown } | null)?.on;
      if (typeof on !== "boolean") return validation("on: true or false");
      stateOf(p.company).seeAll = on;
      return json({ on });
    },
    "memory.decisionCalibration": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const c = stateOf(p.company).checked;
      return json({
        decisions: 12,
        checked: c.worked + c.did_not + c.too_early,
        held_up: c.worked,
        did_not: c.did_not,
        too_early: c.too_early,
        since: at(60 * 24 * 90),
      } satisfies OperationOutput<"memory.decisionCalibration">);
    },
    "memoryPeople.card": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return json(cardOf(p));
    },
    "memoryPeople.profileAccept": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!draftOf(p)) return notFound();
      people.drafts.set(mine(p), null);
      bump(p);
      return json(cardOf(p));
    },
    "memoryPeople.profileDiscard": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      if (!draftOf(p)) return notFound();
      people.drafts.set(mine(p), null);
      return json(cardOf(p));
    },
    "memoryPeople.successions": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return json({ items: successionsOf(p), doors: doorsOf() });
    },
    "memoryPeople.successionAnswer": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const list = successionsOf(p);
      const x = list.find((r) => r.id === params.id);
      if (!x) return notFound();
      if (x.state !== "waiting") return problem(409, "conflict", "Conflict", "Already answered");
      const answer = (body as { answer?: string } | null)?.answer;
      if (answer !== "accept" && answer !== "decline")
        return validation("answer: accept or decline");
      const next: Succession = {
        ...x,
        state: answer === "accept" ? "accepted" : "declined",
        can_answer: false,
      };
      list.splice(list.indexOf(x), 1, next);
      bump(p);
      return json(next);
    },
    "memoryPeople.successionSettle": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const list = successionsOf(p);
      const x = list.find((r) => r.id === params.id);
      if (!x) return notFound();
      const how = (body as { how?: string } | null)?.how;
      if (how !== "cancel" && how !== "apply") return validation("how: cancel or apply");
      const next: Succession = {
        ...x,
        state: how === "cancel" ? "cancelled" : "applied",
        can_answer: false,
        can_settle: false,
      };
      list.splice(list.indexOf(x), 1, next);
      return json(next);
    },
    "memoryPeople.roles": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json({
        items: [
          { seat_id: uid(0x7f10, 1), title: "Account Manager", state: "pending", detail: null },
          { seat_id: uid(0x7f10, 2), title: "Recruiting Lead", state: "pending", detail: null },
        ],
        waiting: 2 + (people.waiting.get(mine(p)) ?? 0),
        doors: doorsOf(),
      });
    },
    "memoryPeople.retirePreview": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json({
        seat_id: params.id,
        title: "Recruiting Lead",
        holders: ["Dana"],
        open_successions: 1,
        waiting_acts: 0,
        records: null,
        doors: doorsOf(),
      });
    },
    "memoryPeople.actingEnd": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json({ items: [], waiting: 0, doors: doorsOf() });
    },
    "memoryPeople.onboarding": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return json(onboardingOf(p));
    },
    "memoryPeople.onboardingStart": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const v = onboardingOf(p);
      const next: Onboarding =
        v.state === "offered" ? { ...v, state: "asking", thread_id: uid(0x7f30, 1) } : v;
      people.onboarding.set(mine(p), next);
      return json(next);
    },
    "memoryPeople.onboardingDismiss": ({ principal: p }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const v = onboardingOf(p);
      const next: Onboarding =
        v.can_dismiss && v.state === "offered" ? { ...v, state: "dismissed" } : v;
      people.onboarding.set(mine(p), next);
      return json(next);
    },
    "memoryPeople.charter": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return json(charterOf(p, params.kind as Charter["subject_kind"], params.id ?? ""));
    },
    "memoryPeople.charterSet": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const kind = params.kind as Charter["subject_kind"];
      const cur = charterOf(p, kind, params.id ?? "");
      if (!cur.can_edit) return problem(403, "forbidden", "Forbidden", "Not allowed to write it");
      const text = String((body as { text?: unknown } | null)?.text ?? "").trim();
      if (!text) return validation("text: required");
      const next: Charter = { ...cur, text, source: "manual", state: "waiting" };
      people.charters.set(charterKey(p, kind, params.id ?? ""), next);
      return json(next);
    },
    "memoryPeople.charterFromVision": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const cur = charterOf(p, "company_bucket", p.company.id);
      const next: Charter = {
        ...cur,
        text: cur.suggested ?? "",
        source: "vision",
        state: "waiting",
      };
      people.charters.set(charterKey(p, "company_bucket", p.company.id), next);
      return json(next);
    },
    "memory.search": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      return search(p, url);
    },
    "promotions.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!member(p)) return problem(403, "forbidden", "Forbidden", "Members only");
      const state = url.searchParams.get("state") ?? "pending";
      if (!["pending", "accepted", "rejected"].includes(state))
        return validation("state: pending, accepted or rejected");
      const rows = stateOf(p.company)
        .promotions.filter((x) => x.state === state)
        .sort((a, b) => b.id.localeCompare(a.id))
        .map((x) => view(p, x));
      return json(page(rows, url));
    },
    "promotions.accept": ({ principal: p, params, body }) => decide(p, params.id, body, true),
    "promotions.reject": ({ principal: p, params, body }) => decide(p, params.id, body, false),
    "recall.status": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json(status(p));
    },
    // CAPTURE-LESS-NOISE: the capture policy by source, and the per-day metric.
    "memory.captureSettings": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json(captureView(stateOf(p.company)));
    },
    "memory.captureUpdate": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const b = (body ?? {}) as Record<string, unknown>;
      const s = stateOf(p.company);
      for (const k of Object.keys(b))
        if (!CAPTURE_SOURCES.includes(k as CaptureSource))
          return validation(`${k}: not a capture source`);
      for (const k of CAPTURE_SOURCES) {
        const v = b[k];
        if (v === undefined) continue;
        if (v === null) delete s.capture[k];
        else if (typeof v === "boolean") s.capture[k] = v;
        else return validation(`${k}: true, false or null`);
      }
      return json(captureView(s));
    },
    "memory.captureStats": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      // A server without Recall has sent nothing; a connected twin shows its last three days.
      const rows = live
        ? [0, 1, 2].flatMap((d) => {
            const day = at(60 * 24 * d).slice(0, 10);
            return [
              {
                day,
                source: "chat" as const,
                captures: 6 - d,
                skipped: 2,
                chars: 41_000,
                records: 9 - d,
                excerpts: 0,
                pending: d === 0 ? 1 : 0,
              },
              {
                day,
                source: "task_threads" as const,
                captures: 3,
                skipped: 1,
                chars: 12_000,
                records: 4,
                excerpts: 0,
                pending: 0,
              },
            ];
          })
        : [];
      return json({ days: 14, rows });
    },
    // RECALL-KB: the twin records the switch; its "worker" syncs at once (no Recall is reached).
    "recall.kbList": ({ principal: p }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      return json({ recall: connection(), interval_minutes: 10, spaces: kbSpaces(p) });
    },
    "recall.kbUpdate": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const item = kbSpaces(p).find((x) => x.space_id === params.id);
      if (!item) return notFound();
      const b = (body ?? {}) as {
        sync_enabled?: unknown;
        agent_access?: unknown;
        opt_out_reason?: unknown;
      };
      if (typeof b.sync_enabled !== "boolean") return validation("sync_enabled is a boolean");
      if (!["none", "read", "read_write"].includes(String(b.agent_access)))
        return validation("agent_access: none, read or read_write");
      // DOCS-ALWAYS-IN-MEMORY: off is an opt-out with a reason (a space already off keeps its own)
      const reason = b.sync_enabled
        ? null
        : typeof b.opt_out_reason === "string" && b.opt_out_reason.trim()
          ? b.opt_out_reason.trim()
          : item.sync_enabled
            ? null
            : item.opt_out_reason;
      if (!b.sync_enabled && !reason) return validation("a reason is required");
      const next: KbSpace = {
        ...item,
        sync_enabled: b.sync_enabled,
        opt_out_reason: reason,
        agent_access: b.agent_access as KbSpace["agent_access"],
      };
      if (b.sync_enabled && !item.sync_enabled && live) synced(next);
      stateOf(p.company).kb.set(next.space_id, next);
      return json(next);
    },
    "recall.kbSyncNow": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return problem(403, "forbidden", "Forbidden", "Managers only");
      const item = kbSpaces(p).find((x) => x.space_id === params.id);
      if (!item) return notFound();
      if (!item.sync_enabled) return problem(409, "conflict", "Conflict", "Sync to Recall is off");
      if (live) synced(item);
      stateOf(p.company).kb.set(item.space_id, item);
      return json(item);
    },
  };
}
